import { extractErrorMessageString } from '@cloudapps/string-helper';
import { Box, Button, Card, CardContent, CircularProgress, Grid, Snackbar, TextField } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { Auth } from 'aws-amplify';
import { useState } from 'react';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';

import { useFormFields } from '../../../../lib/hooks.lib';
import { useAuthStyles } from '../../../../shared/components/auth/styles';

const ChangePhone = (props) => {
  const classes = useAuthStyles();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [codeSent, setCodeSent] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fields, handleFieldChange] = useFormFields({
    phone_number: '',
    code: ''
  });

  const checkSubmitFormFilled = () => {
    return fields.phone_number.indexOf(' ') >= 0;
  };

  const checkConfirmationFormFilled = () => {
    return !!fields.code;
  };

  const formatPhone = (phoneNum: string) => {
    const phoneSub = phoneNum.substring(2);

    return `${phoneSub.slice(0, 3)}-${phoneSub.slice(3, 6)}-${phoneSub.slice(6)}`;
  };

  const resendCode = async (event) => {
    event.preventDefault();
    try {
      await Auth.verifyCurrentUserAttribute('phone_number');
      setSnackBarOpen(true);
    } catch (error) {
      console.error(error);
      setErrorMessage(extractErrorMessageString(error));
    }
  };

  const submitChangePhone = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(currentUser, { phone_number: fields.phone_number });
      setLoading(false);
      setCodeSent(true);
    } catch (error) {
      console.error(error);
      setErrorMessage(extractErrorMessageString(error));
      setLoading(false);
    }
  };

  const submitConfirmationCode = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await Auth.verifyCurrentUserAttributeSubmit('phone_number', fields.code);
      setLoading(false);
      navigate('/applications');
    } catch (error) {
      console.error(error);
      setErrorMessage(extractErrorMessageString(error));
      setLoading(false);
    }
  };

  const renderUpdateForm = () => (
    <form>
      <Grid container justifyContent="space-between" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Change your phone number</Typography>
        </Grid>
        {errorMessage && (
          <Grid item xs={12}>
            <Alert
              severity="error"
              onClose={() => {
                setErrorMessage(undefined);
              }}
            >
              {errorMessage}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <InputMask
            mask="+1-999-999-9999"
            value={fields.phone_number}
            maskChar=" "
            onChange={(evt) => {
              evt.target.value = evt.target.value.replace(/-/g, '');
              handleFieldChange(evt);
            }}
          >
            {() => <TextField name="phone_number" required id="phone_number" label="Mobile Phone (will receive a text)" fullWidth />}
          </InputMask>
        </Grid>
      </Grid>
      <Grid container justifyContent="center">
        <Grid item style={{ marginTop: '20px' }}>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            className={classes.button}
            disabled={checkSubmitFormFilled()}
            onClick={(e) => submitChangePhone(e)}
          >
            Update
            {loading && (
              <Box ml={2} mt={1}>
                <CircularProgress size="0.8rem" color={'inherit'} />
              </Box>
            )}
          </Button>
        </Grid>
      </Grid>
    </form>
  );

  const renderConfirmationForm = () => (
    <form>
      <Grid container justifyContent="space-between" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Enter the 6-digit code we sent to this number:</Typography>
          <Typography variant="body1" className={classes.bold}>
            {formatPhone(fields.phone_number)}
          </Typography>
        </Grid>
        {errorMessage && (
          <Grid item xs={12}>
            <Alert
              severity="error"
              onClose={() => {
                setErrorMessage(undefined);
              }}
            >
              {errorMessage}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid item xs={12}>
            <InputMask mask="999999" disabled={false} maskChar={null} value={fields.code} onChange={handleFieldChange}>
              {() => <TextField autoFocus required id="code" name="code" label="Code" type="code" fullWidth />}
            </InputMask>
          </Grid>
        </Grid>
      </Grid>
      <Link component="button" type="button" onClick={(e) => resendCode(e)} variant="body2" style={{ float: 'right' }}>
        Resend code
      </Link>
      <Grid container justifyContent="center">
        <Grid item style={{ marginTop: '20px' }}>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            className={classes.button}
            disabled={checkConfirmationFormFilled()}
            onClick={(e) => submitConfirmationCode(e)}
          >
            Confirm
            {loading && (
              <Box ml={2} mt={1}>
                <CircularProgress size="0.8rem" color={'inherit'} />
              </Box>
            )}
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        open={snackBarOpen}
        onClose={() => setSnackBarOpen(false)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackBarOpen(false)} severity="success">
          Code re-sent
        </Alert>
      </Snackbar>
    </form>
  );

  return (
    <div role="tabpanel" hidden={props.tab !== 'phone'}>
      <Card>
        <CardContent>{!codeSent ? renderUpdateForm() : renderConfirmationForm()}</CardContent>
      </Card>
    </div>
  );
};

export default ChangePhone;
